import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState } from "react";
import { ApolloProvider as ApolloProviderRaw } from "@apollo/client/react";
import { createApolloClient } from "./createApolloClient.js";
// ApolloProvider is typed against @types/react@18 which lacks bigint in ReactNode.
const ApolloProvider = ApolloProviderRaw;
const PlatformGraphQLContext = createContext(undefined);
/**
 * Internal context carrying the `InMemoryCache` that `PlatformGraphQLProvider`
 * resolved from `initializeCache()`. The downstream `GraphQLProvider` reads
 * this so both providers share a single persisted cache instance — otherwise
 * mobile would run persistence initialization twice against the same
 * AsyncStorage key, racing flushes.
 *
 * `null` is a valid value meaning "no persistence configured" (e.g. web with
 * no `initializeCache` adapter).
 */
const ResolvedApolloCacheContext = createContext(undefined);
/**
 * Provides platform-specific GraphQL configuration via context AND a base
 * ApolloClient (URL: `baseUrlPrefix/mainnet`) for mutations that don't need
 * account-specific context (e.g. MeshProvider's OAuth flows).
 *
 * `GraphQLProvider` (inside `BullrunProviders`) reads the config from this
 * context and overrides the ApolloClient with an account-aware instance.
 */
export const PlatformGraphQLProvider = ({ value, children, }) => {
    const { baseUrlPrefix, initializeCache, webSocketImpl, observability } = value;
    const baseUrl = `${baseUrlPrefix}/mainnet`;
    // Cache init is a separate async phase. When no `initializeCache` adapter
    // is configured (web with no persistence) we short-circuit into "ready"
    // with `resolvedCache = null`.
    const [resolvedCache, setResolvedCache] = useState(initializeCache ? undefined : null);
    useEffect(() => {
        if (!initializeCache)
            return;
        let cancelled = false;
        initializeCache()
            .then((cache) => {
            if (!cancelled)
                setResolvedCache(cache);
        })
            .catch((error) => {
            if (!cancelled) {
                observability.error("Failed to initialize cache persistence:", error);
                // Fall through to no-cache so the app isn't stuck on a loading
                // screen just because persistence failed.
                setResolvedCache(null);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [initializeCache, observability]);
    // Client lifecycle mirrors GraphQLProvider: created inside useEffect so
    // Strict Mode / concurrent rendering can't leak a WebSocket-linked client
    // created in a render that never commits. The cleanup calls `.stop()` on
    // the exact captured instance.
    const [client, setClient] = useState(null);
    useEffect(() => {
        if (resolvedCache === undefined)
            return;
        const nextClient = createApolloClient({
            baseUrl,
            getToken: () => Promise.resolve(null),
            observability,
            webSocketImpl,
            ...(resolvedCache ? { cache: resolvedCache } : {}),
        });
        setClient(nextClient);
        return () => {
            nextClient.stop();
        };
    }, [baseUrl, resolvedCache, observability, webSocketImpl]);
    if (resolvedCache === undefined || !client)
        return null;
    return (_jsx(PlatformGraphQLContext.Provider, { value: value, children: _jsx(ResolvedApolloCacheContext.Provider, { value: resolvedCache, children: _jsx(ApolloProvider, { client: client, children: children }) }) }));
};
export function usePlatformGraphQL() {
    const ctx = useContext(PlatformGraphQLContext);
    if (!ctx) {
        throw new Error("usePlatformGraphQL must be used within a PlatformGraphQLProvider");
    }
    return ctx;
}
/**
 * Returns the `InMemoryCache` that `PlatformGraphQLProvider` resolved at
 * startup, or `null` when no persistence adapter was configured. Used by
 * `GraphQLProvider` so it can reuse a single persisted cache instead of
 * running `initializeCache` a second time.
 */
export function useResolvedApolloCache() {
    const ctx = useContext(ResolvedApolloCacheContext);
    if (ctx === undefined) {
        throw new Error("useResolvedApolloCache must be used within a PlatformGraphQLProvider");
    }
    return ctx;
}
